import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { get } from 'lodash-es'
import { useAuthApiStore } from '@/src/shared/auth/authApiStore'
import * as buyerApi from '@/v31-buyer/sdk.gen'
import type { FullUser, Organisation } from '@/v31-buyer/types.gen'

// Define a more complete user type that includes managedOrgs
interface FullUserWithManaged extends FullUser {
  managedOrgs?: Organisation[]
}

export const useUserProfileStore = defineStore('userProfile', () => {
  const authApiStore = useAuthApiStore()

  // State
  const currentUser = ref<FullUserWithManaged>({} as FullUserWithManaged)
  const _isLoggedIn = ref(false)
  const clientOrgs = ref<Organisation[]>([])

  // Getters
  const isLoggedIn = computed(() => {
    return authApiStore.isAuthenticated
  })

  const currentUserId = computed(() => {
    return authApiStore.currentUserId
  })

  const currentUserToken = computed(() => {
    return authApiStore.accessToken
  })

  const canManageUsers = computed(() => {
    return hasPermission('manageUsers')
  })

  const canEditProjects = computed(() => {
    return hasPermission('editProject')
  })

  const canManageOrganisation = computed(() => {
    return hasPermission('manageOrganisation')
  })

  const hasPermission = (permission: string) => {
    return (
      currentUser.value &&
      get(currentUser.value, 'permissions', []).indexOf(permission) >= 0
    )
  }

  const managedOrgs = computed(() => {
    if (!currentUser.value || !currentUser.value.managedOrgs) return []
    return currentUser.value.managedOrgs
  })

  // Actions
  function setLoggedIn(loggedIn: boolean) {
    _isLoggedIn.value = loggedIn
  }

  function setCurrentUser(user: FullUserWithManaged) {
    currentUser.value = user
    setLoggedIn(true)
  }

  function setClientOrgs(orgArray: Organisation[]) {
    clientOrgs.value = orgArray
  }

  async function verify(targetUser: string | null = null): Promise<void> {
    const userId = targetUser || authApiStore.currentUserId

    if (!userId) {
      return
    }

    // No try/catch - let errors propagate up to be handled by higher-level handlers
    const response = await buyerApi.getUser({
      query: { userId },
    })

    if (response.data) {
      setCurrentUser(response.data)
      await getCurrentUserClientOrgs()
    }
  }

  async function login(targetUser: { userId: string }) {
    // Use our existing userId to get user information
    await verify(targetUser.userId)

    // Check if verification was successful by checking if user data was set
    if (currentUser.value && Object.keys(currentUser.value).length > 0) {
      return true
    }

    return false
  }

  function logout() {
    authApiStore.logout()
    setLoggedIn(false)
    currentUser.value = {} as FullUserWithManaged
    clientOrgs.value = []
  }

  async function getCurrentUserClientOrgs() {
    if (!currentUserId.value) return []

    if (clientOrgs.value.length) return clientOrgs.value

    // Use the getOrganisations API function from the v31-buyer SDK
    const response = await buyerApi.getOrganisations({
      query: { orgType: 'clients' },
    })

    if (response.data) {
      setClientOrgs(response.data as Organisation[])
      clientOrgs.value.push(currentUser.value.organisation)
      return response.data
    }

    return []
  }

  async function getOrganisation(orgId: string) {
    // Use the getOrganisation API function from the v31-buyer SDK
    const response = await buyerApi.getOrganisation({
      query: { orgId },
    })

    return response.data
  }

  async function getOrganisations(orgType: 'clients' | 'vendors') {
    // Use the getOrganisations API function from the v31-buyer SDK
    const response = await buyerApi.getOrganisations({
      query: { orgType },
    })

    return response.data
  }

  async function updateOrganisation(org: Organisation) {
    // Use the putOrganisation API function from the v31-buyer SDK
    const response = await buyerApi.putOrganisation({
      body: org,
    })

    return response.data
  }

  return {
    currentUser,
    _isLoggedIn,
    clientOrgs,
    isLoggedIn,
    currentUserId,
    currentUserToken,
    canManageUsers,
    canEditProjects,
    canManageOrganisation,
    hasPermission,
    managedOrgs,
    setLoggedIn,
    setCurrentUser,
    setClientOrgs,
    verify,
    login,
    logout,
    getCurrentUserClientOrgs,
    getOrganisation,
    getOrganisations,
    updateOrganisation,
  }
})
